import { useSearch, useNavigate } from "@tanstack/react-router"
import {
  Building2,
  Loader2,
  ChevronLeft,
  FileText,
  MapPin,
  CheckCircle2,
  XCircle,
  Clock,
  ExternalLink,
} from "lucide-react"

import { useGetAdminCompanyDetails } from "@/hooks/admin/useGetAdminCompanyDetails"
import { useUpdateCompanyVerification } from "@/hooks/admin/useUpdateCompanyVerification"
import { useUpdateCompanyStatus } from "@/hooks/admin/useUpdateCompanyStatus"
import { useAuthStore } from "@/store/auth.store"
import { getCompanyVerificationDocUrl } from "@/lib/api/company.api"
import { toast } from "sonner"

function formatDate(isoStr: string | null | undefined) {
  if (!isoStr) return "-"
  return new Date(isoStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function CompanyVerificationDetailView({
  companyId,
  onBack,
}: {
  companyId: string
  onBack: () => void
}) {
  const { data: company, isLoading, error } = useGetAdminCompanyDetails(companyId)
  const updateMutation = useUpdateCompanyVerification()
  const companyStatusMutation = useUpdateCompanyStatus()
  const accessToken = useAuthStore((s) => s.accessToken)

  if (isLoading) {
    return (
      <div className="relative flex flex-col items-center justify-center py-20 text-slate-400 gap-3 bg-white border border-border rounded-2xl shadow-sm w-full max-w-5xl mx-auto font-sans text-left min-h-[300px]">
        <div className="absolute top-5 left-5">
          <button
            onClick={onBack}
            className="flex items-center justify-center p-2 rounded-lg border border-border bg-white hover:bg-primary/5 hover:text-primary hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer text-slate-800"
            title="Back to Companies"
          >
            <ChevronLeft className="size-4" />
          </button>
        </div>
        <Loader2 className="size-8 animate-spin text-primary" />
        <p className="text-sm font-medium text-slate-500 font-sans">Fetching company details...</p>
      </div>
    )
  }

  if (error || !company) {
    return (
      <div className="w-full max-w-5xl mx-auto space-y-4 font-sans text-left animate-in fade-in duration-200">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center justify-center p-2 rounded-lg border border-border bg-white hover:bg-primary/5 hover:text-primary hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer text-slate-800"
            title="Back to Companies"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-destructive/20 bg-destructive/10 p-5 text-sm text-destructive">
          <XCircle className="size-5 shrink-0" />
          <p className="font-semibold">Failed to load company details.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full animate-in fade-in duration-200 text-left font-sans max-w-5xl mx-auto">
      <div className="group relative overflow-hidden bg-white border border-border hover:border-primary/30 rounded-2xl p-5 space-y-4 shadow-sm hover:shadow-md transition-all duration-300">
        <div className="absolute top-0 left-0 right-0 h-[1px] group-hover:h-[3.5px] bg-gradient-to-r from-primary to-primary-hover rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={onBack}
              className="flex items-center justify-center p-2 rounded-lg border border-border bg-white hover:bg-primary/5 hover:text-primary hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer text-slate-800 shrink-0"
              title="Back to Companies"
            >
              <ChevronLeft className="size-4" />
            </button>
            <div className="min-w-0 flex items-start gap-1.5">
              <div className="text-[12px] font-bold text-slate-500 uppercase tracking-wider shrink-0">
                COMPANY:
              </div>
              <div className="text-[12px] font-bold text-slate-500 uppercase tracking-wider break-words min-w-0" title={company.name}>
                {company.name}
              </div>
            </div>
          </div>

          <div className="flex flex-row items-center justify-between md:justify-end gap-3 w-full md:w-auto shrink-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[12px] font-bold uppercase tracking-wider text-slate-400">STATUS</span>
              <span className={`h-7 px-2.5 inline-flex items-center justify-center gap-1 rounded-lg text-[10px] font-bold border uppercase tracking-wider shrink-0 ${
                company.verificationStatus === "APPROVED" ? "bg-emerald-50 text-emerald-700 border-emerald-100" :
                company.verificationStatus === "REJECTED" ? "bg-red-50 text-red-700 border-red-100" :
                company.verificationStatus === "PENDING" ? "bg-amber-50 text-amber-700 border-amber-100" :
                company.verificationStatus === "SUBMITTED" ? "bg-blue-50 text-blue-700 border-blue-100" :
                "bg-slate-50 text-slate-700 border-slate-200"
              }`}>
                {company.verificationStatus === "APPROVED" ? <CheckCircle2 className="size-3 shrink-0" /> :
                 company.verificationStatus === "REJECTED" ? <XCircle className="size-3 shrink-0" /> :
                 <Clock className="size-3 shrink-0" />}
                <span>{company.verificationStatus || "NOT SUBMITTED"}</span>
              </span>
            </div>

            <select
              value={company.verificationStatus || "PENDING"}
              onChange={(e) => {
                companyStatusMutation.mutate({ id: company.id, status: e.target.value })
              }}
              className="h-7 px-2.5 text-[10px] font-bold uppercase tracking-wider border border-slate-200 rounded-lg bg-white text-slate-700 outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all cursor-pointer font-sans"
              disabled={companyStatusMutation.isPending}
            >
              <option value="PENDING">PENDING</option>
              <option value="SUBMITTED">SUBMITTED</option>
              <option value="APPROVED">APPROVED</option>
              <option value="REJECTED">REJECTED</option>
            </select>
          </div>
        </div>

        <div className="h-px bg-border w-full" />

        <div className="space-y-6">
          <div className="space-y-6">
            <div className="space-y-1.5">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <Building2 className="size-3.5 text-primary" /> Company Overview
              </h4>
              <p className="text-sm text-slate-700 font-medium bg-primary/[0.02] p-3.5 rounded-xl border border-border hover:border-primary/30 transition-colors leading-relaxed italic">
                {company.description || "No description provided."}
              </p>
              
              {company.tags && company.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1.5">
                  {company.tags.map((t: string) => (
                    <span key={t} className="rounded px-2 py-1 text-[9px] font-bold bg-primary/5 text-primary border border-primary/20 uppercase tracking-wide">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <MapPin className="size-3.5 text-primary" /> Contact & Address
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50/50 border border-slate-200/50 p-4 rounded-xl">
                {company.email && (
                  <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-primary/20 transition-colors">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/5 text-primary">
                      <FileText className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Email Address</p>
                      <p className="text-xs font-bold text-slate-750">{company.email}</p>
                    </div>
                  </div>
                )}
                
                {company.phoneNumber && (
                  <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-primary/20 transition-colors">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/5 text-primary">
                      <FileText className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Phone Number</p>
                      <p className="text-xs font-bold text-slate-750">{company.phoneNumber}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-primary/20 transition-colors md:col-span-2">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/5 text-primary">
                    <MapPin className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Address</p>
                    <p className="text-xs font-bold text-slate-750 whitespace-pre-wrap leading-tight">
                      {[company.addressLine1, company.city, company.state, company.country].filter(Boolean).join(", ") || "N/A"}
                    </p>
                  </div>
                </div>

                {company.website && (
                  <div className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-100 shadow-sm hover:border-primary/20 transition-colors md:col-span-2">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/5 text-primary">
                      <ExternalLink className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Website</p>
                      <a href={company.website} target="_blank" rel="noreferrer" className="text-xs font-bold text-primary hover:text-primary-hover hover:underline">
                        {company.website}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {company.verification && (Array.isArray(company.verification) ? company.verification.length > 0 : true) ? (
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <FileText className="size-3.5 text-primary" /> Verification Documents
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(() => {
                    const verifications = Array.isArray(company.verification)
                      ? company.verification
                      : [company.verification]

                    return verifications.map((doc: any, idx: number) => (
                      <div key={doc.id || idx} className="bg-slate-50/50 border border-slate-200/50 rounded-xl p-4 flex flex-col justify-between shadow-sm">
                        <div className="space-y-3 text-sm font-semibold text-slate-700">
                          <div className="flex justify-between items-center pb-2 border-b border-slate-200/50">
                            <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Document Type</span>
                            <button 
                              onClick={async () => {
                                try {
                                  const res = await getCompanyVerificationDocUrl(company.id, doc.id, accessToken || "")
                                  if (res?.url) {
                                    window.open(res.url, "_blank", "noopener,noreferrer")
                                  }
                                } catch (error) {
                                  console.error("Failed to get verification document URL:", error)
                                  toast.error("Failed to fetch verification document")
                                }
                              }}
                              className="text-xs font-bold text-primary hover:text-primary-hover hover:underline inline-flex items-center justify-center gap-1 cursor-pointer bg-transparent border-none p-0"
                            >
                              {doc.documentType} <ExternalLink className="size-4" />
                            </button>
                          </div>
                          <div className="flex justify-between items-center pb-2 border-b border-slate-200/50">
                            <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Identifier</span>
                            <span className="text-xs">{doc.documentIdentifier}</span>
                          </div>
                          <div className="flex justify-between items-center pb-2 border-b border-slate-200/50">
                            <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Submitted On</span>
                            <span className="text-xs">{formatDate(doc.createdAt)}</span>
                          </div>
                          <div className="flex justify-between items-center pt-1">
                            <span className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Status</span>
                            <div className="flex items-center gap-2">
                              <span className={`h-7 px-2.5 inline-flex items-center justify-center text-[10px] font-bold border rounded-lg uppercase tracking-wider shrink-0 ${
                                doc.status === "APPROVED" ? "bg-emerald-50 text-emerald-700 border-emerald-100" :
                                doc.status === "REJECTED" ? "bg-red-50 text-red-700 border-red-100" :
                                doc.status === "PENDING" ? "bg-amber-50 text-amber-700 border-amber-100" :
                                "bg-slate-50 text-slate-700 border-slate-200"
                              }`}>
                                {doc.status}
                              </span>
                              <select
                                value={doc.status}
                                onChange={(e) => {
                                  updateMutation.mutate({ id: doc.id, status: e.target.value as any })
                                }}
                                className="h-7 px-2.5 text-[10px] font-bold uppercase tracking-wider border border-slate-200 rounded-lg bg-white text-slate-700 outline-none focus:border-primary transition-colors cursor-pointer"
                                disabled={updateMutation.isPending}
                              >
                                <option value="PENDING">PENDING</option>
                                <option value="APPROVED">APPROVED</option>
                                <option value="REJECTED">REJECTED</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  })()}
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 border border-slate-200/50 rounded-xl p-6 text-center shadow-sm">
                <FileText className="size-8 text-slate-300 mx-auto mb-2" />
                <h3 className="text-[11px] font-black uppercase tracking-wider text-slate-500">No Verification Submitted</h3>
                <p className="text-xs text-slate-400 mt-1 font-medium">This company has not uploaded documents yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function CompanyDetailsPage({ companyId }: { companyId: string }) {
  const navigate = useNavigate()

  const handleGoBack = () => {
    navigate({ to: "/companies" })
  }

  return (
    <CompanyVerificationDetailView companyId={companyId} onBack={handleGoBack} />
  )
}
